/**
 * NewsletterSignup — public footer signup that uses the federated
 * Ekselens-as-hub list. One signup writes to ERIO + propagates to the
 * shared list on www.ekselens.com via the existing lead forwarder, so
 * subscribers only need to manage one preference.
 */
import { useState } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Send } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export const NewsletterSignup = ({ source = "footer" }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState({ status: "idle", error: null,
                                       alreadySubscribed: false });

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setState({ status: "sending", error: null });
    try {
      const r = await axios.post(`${API}/api/newsletter/subscribe`, {
        email: trimmed,
        source,
      });
      setState({
        status: "done", error: null,
        alreadySubscribed: r.data.already_subscribed,
      });
    } catch (err) {
      setState({
        status: "error",
        error: err.response?.data?.detail || "Sign-up failed",
      });
    }
  };

  if (state.status === "done") {
    return (
      <div
        className="rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
        data-testid="newsletter-confirmation"
      >
        <CheckCircle2 className="-mt-0.5 mr-1 inline h-3.5 w-3.5" />
        {state.alreadySubscribed
          ? "You're already on the list — thanks for double-checking."
          : "Subscribed. One list across erio.ekselens.com + ekselens.com."}
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-2 sm:flex-row sm:items-center"
      data-testid="newsletter-signup-form"
    >
      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
        placeholder="you@company.com"
        className="min-w-[220px] flex-1 rounded-md border border-black/10 bg-white px-3 py-1.5 text-sm placeholder-ek-ink/40 focus:border-ek-purple focus:outline-none"
        data-testid="newsletter-email"
      />
      <button
        type="submit"
        disabled={state.status === "sending"}
        className="inline-flex items-center justify-center gap-1.5 rounded-md bg-ek-purple px-3 py-1.5 text-xs font-semibold text-white hover:bg-ek-purple-dark disabled:opacity-50"
        data-testid="newsletter-submit"
      >
        {state.status === "sending"
          ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
          : <Send className="h-3.5 w-3.5" />}
        Subscribe
      </button>
      {state.status === "error" && (
        <p className="text-xs text-rose-700" data-testid="newsletter-error">
          {state.error}
        </p>
      )}
    </form>
  );
};
